import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { SystemNote } from '../types/systemNote';

interface SystemNotesContextType {
  systemNotes: SystemNote[];
  addSystemNote: (instruction: string, question: string, mentionedPerson?: string) => string;
  getRelevantNotes: (question: string) => SystemNote[];
  markNoteUsed: (id: string) => void;
  deleteSystemNote: (id: string) => void;
}

const SystemNotesContext = createContext<SystemNotesContextType | undefined>(undefined);

export const useSystemNotes = () => {
  const context = useContext(SystemNotesContext);
  if (!context) {
    throw new Error('useSystemNotes must be used within SystemNotesProvider');
  }
  return context;
};

interface SystemNotesProviderProps {
  children: ReactNode;
}

export const SystemNotesProvider: React.FC<SystemNotesProviderProps> = ({ children }) => {
  const [systemNotes, setSystemNotes] = useState<SystemNote[]>([
    // Mock initial system notes for demo
    {
      id: 'sys-note-1',
      title: 'Release-Cycle Commitments',
      summary: 'For questions about release-cycle commitments or timeline acceleration, start with a broad scan to generate a conservative draft, then refine by prioritizing the Q3/Q4 planning discussion in #project-updates and the latest rollout notes.',
      originalInstruction: 'For questions about release-cycle commitments or timeline acceleration: Start with a broad scan to generate a conservative draft, then refine by prioritizing the Q3/Q4 planning discussion in #project-updates and the latest rollout notes. Avoid making commitments. Provide conditions required to commit (capacity, dependencies, security, approvals, stakeholder alignment) and recommend the next step for confirmation. Source Preference: Initial Context = org policies + roadmap docs. Refinement Primary Context = Teams planning thread + rollout notes. Supporting = strategic planning docs.',
      question: '@Umer, can we commit to supporting this integration in the next release cycle?',
      category: 'source-preference',
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2),
      updatedAt: new Date(),
      usageCount: 3,
      lastUsedAt: new Date(Date.now() - 1000 * 60 * 60 * 12),
    },
    {
      id: 'sys-note-2',
      title: 'Detailed Q3/Q4 Context',
      summary: 'For quarterly planning questions, always include specific progress metrics, resource allocation details, and stakeholder alignment information',
      originalInstruction: 'Add more context about Q3 targets',
      question: 'What are the Q3 API targets?',
      category: 'content-preference',
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5),
      updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5),
      usageCount: 5,
      lastUsedAt: new Date(Date.now() - 1000 * 60 * 60 * 6),
    },
  ]);

  const addSystemNote = useCallback((instruction: string, question: string, mentionedPerson?: string): string => {
    const noteId = `sys-note-${Date.now()}`;

    // Generate a summary of the instruction
    const summary = generateInstructionSummary(instruction, question);

    // Determine category based on instruction content
    const category = categorizeInstruction(instruction);

    // Generate title
    const title = generateNoteTitle(instruction, question, mentionedPerson);

    const newNote: SystemNote = {
      id: noteId,
      title,
      summary,
      originalInstruction: instruction,
      question,
      category,
      createdAt: new Date(),
      updatedAt: new Date(),
      usageCount: 0,
    };

    setSystemNotes(prev => [newNote, ...prev]);
    return noteId;
  }, []);

  const getRelevantNotes = useCallback((question: string): SystemNote[] => {
    const lowerQuestion = question.toLowerCase();

    // Find notes that are relevant to the current question
    return systemNotes.filter(note => {
      const noteKeywords = [
        ...note.summary.toLowerCase().split(' '),
        ...note.question.toLowerCase().split(' '),
        ...(note.category || '').toLowerCase().split(' '),
      ];

      const questionWords = lowerQuestion.split(' ');

      // Check if there's overlap between note keywords and question words
      const hasOverlap = questionWords.some(word =>
        word.length > 3 && noteKeywords.some(keyword => keyword.includes(word) || word.includes(keyword))
      );

      return hasOverlap;
    }).sort((a, b) => {
      // Sort by usage count and recency
      if (b.usageCount !== a.usageCount) {
        return b.usageCount - a.usageCount;
      }
      return (b.lastUsedAt?.getTime() || 0) - (a.lastUsedAt?.getTime() || 0);
    });
  }, [systemNotes]);

  const markNoteUsed = useCallback((id: string) => {
    setSystemNotes(prev =>
      prev.map(note =>
        note.id === id
          ? {
            ...note,
            usageCount: note.usageCount + 1,
            lastUsedAt: new Date(),
            updatedAt: new Date(),
          }
          : note
      )
    );
  }, []);

  const deleteSystemNote = useCallback((id: string) => {
    setSystemNotes(prev => prev.filter(note => note.id !== id));
  }, []);

  return (
    <SystemNotesContext.Provider
      value={{
        systemNotes,
        addSystemNote,
        getRelevantNotes,
        markNoteUsed,
        deleteSystemNote,
      }}
    >
      {children}
    </SystemNotesContext.Provider>
  );
};

// Helper function to generate a summary from instruction
function generateInstructionSummary(instruction: string, question: string): string {
  const lowerInstruction = instruction.toLowerCase();
  const lowerQuestion = question.toLowerCase();

  // Check for release-cycle commitments or timeline acceleration questions
  const isReleaseCycleQuestion = lowerQuestion.includes('release-cycle') ||
    lowerQuestion.includes('release cycle') ||
    lowerQuestion.includes('timeline acceleration') ||
    lowerQuestion.includes('accelerate') && (lowerQuestion.includes('timeline') || lowerQuestion.includes('rollout') || lowerQuestion.includes('q3') || lowerQuestion.includes('q4'));

  if (isReleaseCycleQuestion && (lowerInstruction.includes('broad scan') || lowerInstruction.includes('conservative') || lowerInstruction.includes('refine'))) {
    return 'For questions about release-cycle commitments or timeline acceleration, start with a broad scan to generate a conservative draft, then refine by prioritizing the Q3/Q4 planning discussion in #project-updates and the latest rollout notes.';
  }

  // Extract key concepts
  if (lowerInstruction.includes('teams') || lowerInstruction.includes('chat')) {
    // Check for Q3/Q4 Planning Discussion and rollout notes instruction
    const isQ3Q4PlanningInstruction = (lowerInstruction.includes('q3/q4') || (lowerInstruction.includes('q3') && lowerInstruction.includes('q4'))) &&
      (lowerInstruction.includes('planning discussion') || lowerInstruction.includes('project-updates')) &&
      (lowerInstruction.includes('rollout notes') || lowerInstruction.includes('rollout'));

    if (isQ3Q4PlanningInstruction) {
      return 'For questions about release-cycle commitments or timeline acceleration, start with a broad scan to generate a conservative draft, then refine by prioritizing the Q3/Q4 planning discussion in #project-updates and the latest rollout notes.';
    }

    if (lowerQuestion.includes('q3') || lowerQuestion.includes('q4') || lowerQuestion.includes('api target')) {
      return 'When questions involve Q3/Q4 planning or API targets, prefer referencing Teams chat discussions for more comprehensive context';
    }
    return 'Prefer referencing Teams chat discussions when available for more detailed context';
  }

  if (lowerInstruction.includes('detail') || lowerInstruction.includes('more context') || lowerInstruction.includes('elaborate')) {
    if (lowerQuestion.includes('q3') || lowerQuestion.includes('q4')) {
      return 'For quarterly planning questions, always include specific progress metrics, resource allocation details, and stakeholder alignment information';
    }
    return 'Provide more detailed context and comprehensive information in responses';
  }

  if (lowerInstruction.includes('rewrite') || lowerInstruction.includes('rewritten')) {
    return 'Rewrite responses with improved clarity and better structure';
  }

  // Default summary
  return `Apply instruction: ${instruction.substring(0, 100)}${instruction.length > 100 ? '...' : ''}`;
}

// Helper function to categorize instruction
function categorizeInstruction(instruction: string): string {
  const lower = instruction.toLowerCase();

  if (lower.includes('teams') || lower.includes('chat') || lower.includes('email') || lower.includes('sharepoint')) {
    return 'source-preference';
  }

  if (lower.includes('detail') || lower.includes('context') || lower.includes('elaborate') || lower.includes('more')) {
    return 'content-preference';
  }

  if (lower.includes('rewrite') || lower.includes('tone') || lower.includes('style')) {
    return 'response-style';
  }

  return 'general';
}

// Helper function to generate note title
function generateNoteTitle(instruction: string, question: string, mentionedPerson?: string): string {
  const lowerInstruction = instruction.toLowerCase();
  const lowerQuestion = question.toLowerCase();

  // Extract key topic from question
  let topic = '';
  if (lowerQuestion.includes('release-cycle') || lowerQuestion.includes('release cycle') ||
    (lowerQuestion.includes('accelerate') && (lowerQuestion.includes('timeline') || lowerQuestion.includes('rollout')))) {
    topic = 'Release-Cycle Commitments & Timeline Acceleration';
  } else if (lowerQuestion.includes('q3') || lowerQuestion.includes('q4')) {
    topic = 'Q3/Q4 Planning';
  } else if (lowerQuestion.includes('api target')) {
    topic = 'API Targets';
  } else if (lowerQuestion.includes('kpi')) {
    topic = 'KPIs';
  } else if (lowerQuestion.includes('deployment') || lowerQuestion.includes('timeline')) {
    topic = 'Deployment Timeline';
  } else {
    topic = 'General';
  }

  // Extract instruction type
  let instructionType = '';
  if (lowerInstruction.includes('teams') || lowerInstruction.includes('chat')) {
    instructionType = 'Teams Chat Context';
  } else if (lowerInstruction.includes('detail') || lowerInstruction.includes('context')) {
    instructionType = 'Detailed Context';
  } else if (lowerInstruction.includes('rewrite')) {
    instructionType = 'Response Style';
  } else {
    instructionType = 'Instruction';
  }

  return `${instructionType} - ${topic}`;
}

